import React, { useState, useMemo, Dispatch } from 'react';
import { useTranslation } from 'react-i18next';

// --- 🛰️ OS CORE ENGINES ---
import { AppState, AppAction, UserProfile, TemplateType, DataScope, WidgetId } from '@/types/index';
import { useDashboardLayout } from '../features/dashboard-engine/hooks/useDashboardLayout';

// --- 🧱 UI COMPONENTS ---
import { DashboardToolbar } from '../features/dashboard-engine/components/DashboardToolbar';
import { DashboardGrid } from '../features/dashboard-engine/components/DashboardGrid';
import { WidgetSidebar } from '../features/dashboard-engine/components/WidgetSidebar';

// --- 🧩 WIDGET REGISTRY ---
import { CalendarAgenda } from '../features/dashboard-engine/widgets/CalendarAgenda';
import { TaskPulse } from '../features/dashboard-engine/widgets/TaskPulse';
import { ActivityFeed } from '../features/dashboard-engine/widgets/ActivityFeed';
import { MaintenanceStatus } from '../features/dashboard-engine/widgets/MaintenanceStatus';
import { SparkEngine } from '../features/dashboard-engine/widgets/SparkEngine';
import { QuickActions } from '../features/dashboard-engine/widgets/QuickActions';

interface DashboardProps {
  state: AppState;
  dispatch: Dispatch<AppAction>;
}

export const Dashboard: React.FC<DashboardProps> = ({ state, dispatch }) => {
  const { t } = useTranslation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // 🛡️ HYBRID SCOPING STATE
  // If no household is active, scope is locked to PERSONAL.
  const activeScope = state.activeDataScope || 'PERSONAL';

  // 1. 🛰️ Initialize Layout Engine
  const {
    layouts,
    isEditMode,
    hasUnsavedChanges,
    toggleEditMode,
    onLayoutChange,
    addWidget,
    removeWidget,
    saveLayout,
    resetLayout
  } = useDashboardLayout(state.currentUser?.id, state.currentUser?.dashboardLayout);

  /**
   * 🛡️ DATA PARTITIONING LOGIC
   * Filters global state feature arrays based on the currently selected Scope.
   * Personal scope targets /users/{uid}, Shared scope targets /households/{hid}.
   */
  const scopedTasks = useMemo(() => {
    return state.tasks.filter(t => {
      const isPersonal = t.scope === 'personal' || !t.scope;
      if (activeScope === 'PERSONAL') return isPersonal;
      if (activeScope === 'SHARED') return !isPersonal;
      return true; // BOTH
    });
  }, [state.tasks, activeScope]);

  const scopedEvents = useMemo(() => {
    return state.calendarEvents.filter(e => {
      const isPersonal = e.scope === 'personal' || !e.scope;
      if (activeScope === 'PERSONAL') return isPersonal;
      if (activeScope === 'SHARED') return !isPersonal;
      return true;
    });
  }, [state.calendarEvents, activeScope]);

  // 2. 🧠 Component Mapping (Scope Aware)
  const widgetRegistry = useMemo((): Partial<Record<WidgetId, React.ReactNode>> => {
    return {
      quick_access: (
        <QuickActions 
          dispatch={dispatch}
          currentUserId={state.currentUser!.id}
          currentHouseholdId={state.currentHouseholdId}
          activeScopeToggle={activeScope === 'PERSONAL' ? 'personal' : activeScope === 'SHARED' ? 'shared' : 'both'} // 🛡️ Ensure quick actions target the correct partition
          onAction={(type, scope) => {
            console.log(`[${scope}] Quick action: ${type} triggered.`);
            // You might want to add dispatch logic here based on type and scope
          }}
        />
      ),
      calendar_agenda: (
                <CalendarAgenda
          events={scopedEvents}
          onEventClick={(ev) => console.log('Teleport to Event:', ev)}
          activeScope={activeScope}
          currentHouseholdId={state.currentHouseholdId}
        />
      ),
      tasks_summary: (
        <TaskPulse 
          tasks={scopedTasks.filter(t => t.templateType === TemplateType.ACTION)}
          currentUserId={state.currentUser!.id}
          currentHouseholdId={state.currentHouseholdId}
        />
      ),
      maintenance_agenda: (
        <MaintenanceStatus 
          tasks={scopedTasks.filter(t => t.templateType === TemplateType.MAINTENANCE)}
          activeScope={activeScope}
          currentUserId={state.currentUser!.id}
          currentHouseholdId={state.currentHouseholdId}
        />
      ),
      activity_log: (
        <ActivityFeed 
          logs={state.auditLogs?.filter(l => {
             if (activeScope === 'PERSONAL') return l.scope === 'personal';
             if (activeScope === 'SHARED') return l.scope === 'household';
             return true;
          }) || []} 
          currentHouseholdId={state.currentHouseholdId}
          activeScope={activeScope}
        />
      ),
      sparks: (
        <SparkEngine
          insights={state.intelligence?.sparks || []}
          currentHouseholdId={state.currentHouseholdId}
          activeScope={activeScope}
        />
      )
    };
  }, [state, dispatch, scopedTasks, scopedEvents, activeScope]);

  // 3. 🛡️ Permission & Sync Guard
  if (!state.currentUser) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="text-center space-y-4">
          <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-[10px] font-black uppercase tracking-[0.4em] text-slate-400">
            {t('auth.syncing', 'Authenticating Neural Link...')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-slate-50 dark:bg-slate-950 transition-colors duration-500">
      
      {/* 🛠️ NAVIGATION & CONTROL NEXUS */}
      <DashboardToolbar 
        isEditMode={isEditMode}
        onToggleEdit={toggleEditMode}
        onResetLayout={resetLayout}
        onSaveLayout={() => {
          saveLayout();
          dispatch({ type: 'UPDATE_LAYOUT', payload: layouts });
        }}
        onAddWidgetClick={() => setIsSidebarOpen(true)}
        hasUnsavedChanges={hasUnsavedChanges}
        lastSynced={state.lastSyncTimestamp ? new Date(state.lastSyncTimestamp) : undefined}
        // 🛡️ HYBRID SCOPE TOGGLE
        activeScope={activeScope}
        onScopeChange={(scope: DataScope) => dispatch({ type: 'SET_DATA_SCOPE', payload: scope })}
        currentHouseholdId={state.currentHouseholdId}
      />

      <div className="flex flex-1 relative overflow-hidden">
        
        {/* 🗓️ THE MODULAR GRID */}
        <main className="flex-1 overflow-y-auto no-scrollbar pb-24">
          {/* 🛡️ Privacy Banner: Informs user which data partition is currently active */}
          <div className="max-w-[1600px] mx-auto px-6 pt-4">
            <div className={`flex items-center gap-3 p-3 rounded-2xl border ${
              activeScope === 'PERSONAL' 
              ? 'bg-emerald-50 border-emerald-100 dark:bg-emerald-900/20 dark:border-emerald-800 text-emerald-600' 
              : 'bg-indigo-50 border-indigo-100 dark:bg-indigo-900/20 dark:border-indigo-800 text-indigo-600'
            }`}>
              <div className={`w-2 h-2 rounded-full animate-pulse ${activeScope === 'PERSONAL' ? 'bg-emerald-500' : 'bg-indigo-500'}`} />
              <span className="text-[9px] font-black uppercase tracking-widest italic">
                {activeScope === 'PERSONAL' ? 'Private Partition' : activeScope === 'SHARED' ? 'Household Hub' : 'Hybrid Stream'} Active
              </span>
            </div>
          </div>

          <DashboardGrid 
            layouts={layouts}
            isEditMode={isEditMode}
            onLayoutChange={onLayoutChange}
            onRemoveWidget={removeWidget}
            widgetRegistry={widgetRegistry}
            currentUserId={state.currentUser!.id}
            currentHouseholdId={state.currentHouseholdId}
            activeScope={activeScope}
          />
        </main>

        {/* 🗄️ WIDGET LIBRARY SIDEBAR */}
        <WidgetSidebar 
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          onAddWidget={(id) => {
            addWidget(id);
            setIsSidebarOpen(false);
          }}
          activeWidgetIds={layouts.map(l => l.i)}
          currentHouseholdId={state.currentHouseholdId}
        />
      </div>

      {/* 🛰️ PERSISTENCE TOAST */}
      {hasUnsavedChanges && !isEditMode && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[100] animate-in slide-in-from-bottom-8 duration-500">
          <button 
            onClick={() => {
              saveLayout();
              dispatch({ type: 'UPDATE_LAYOUT', payload: layouts });
            }}
            className="bg-indigo-600 text-white px-8 py-4 rounded-[2rem] font-black text-[10px] uppercase tracking-widest shadow-2xl shadow-indigo-500/40 hover:bg-indigo-700 hover:scale-105 transition-all active:scale-95 flex items-center gap-3"
          >
            <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
            Commit Layout Changes
          </button>
        </div>
      )}
    </div>
  );
};